import { rolesModel, userClaimInformation } from "../../models/usersModel";
import { getUsers, updateUser } from "../../services/usersService";
import { extractCompanyLogo, extractImageFromS3 } from "./extractCompanyLogo";
import { extractCompanyId } from "./extractCompanyId";
import { ConfigurationDispatcher } from "../../redux/dispatcher/configurationDispatcher";
import { actionTypesConfigurations } from "../../redux/constants/actionTypesConfigurations";
import { getItemFromSessionStorage } from "../../services/utils/storageService";

const setUserInfo = (data: userClaimInformation) => {
  ConfigurationDispatcher(actionTypesConfigurations.UserInfo, data);
};

const setUsersRoles = (data: Array<rolesModel>) => {
  const roles: Array<string> = [];
  data.map((r: rolesModel) => {
    roles.push(r.role);
  });
  ConfigurationDispatcher(actionTypesConfigurations.UserRoles, roles);
};

const setHasAvatar = (data: boolean) => {
  ConfigurationDispatcher(actionTypesConfigurations.HasAvatar, data);
};

const setProfilePhoto = (data: string) => {
  ConfigurationDispatcher(actionTypesConfigurations.ProfilePhoto, data);
};

const setCurrentRole = (data: string) => {
  ConfigurationDispatcher(actionTypesConfigurations.CurrentRole, data);
};

const setLanguage = (data: string) => {
  ConfigurationDispatcher(actionTypesConfigurations.Language, data);
};

// Populates every piece of redux state the app needs (profile, roles,
// company, logo, avatar) from an already-known user document. LoginScreen
// calls this directly with the user object /users/login now returns — no
// extra round trip needed. InitUser (below) calls it after fetching the
// user, for the page-reload / persisted-session path where there's no
// login response to use directly.
export const applyLoggedInUser = (
  user: userClaimInformation,
  setLogo: (e: string) => void,
): Promise<void> => {
  setUserInfo(user);
  setUsersRoles(user.roles || []);

  // Passing empty user object will sync user data from the backend
  user.auto_sync && updateUser({}, "");

  extractCompanyId().then((_companyItem) => {
    extractCompanyLogo(
      _companyItem.companyId,
      setLogo,
      setCurrentRole,
      user.roles || [],
    );
  });

  return new Promise((resolve) => {
    if (user.has_avatar) {
      extractImageFromS3("avatar", user.user_id).then((data) => {
        setProfilePhoto(data?.signedUrl);
        setHasAvatar(true);
        resolve();
      });
    } else {
      setHasAvatar(false);
      resolve();
    }
  });
};

// Page-reload / already-has-a-valid-session path: fetch the user via the
// API, then apply it. A fresh login should call applyLoggedInUser directly
// instead (see LoginScreen.tsx).
export const InitUser = (setLogo: (e: string) => void) => {
  const storedLanguageContext =
    getItemFromSessionStorage("language-context") || "en";
  setLanguage(storedLanguageContext);

  return getUsers(false).then((mapiData) => {
    const { users }: { users: Array<userClaimInformation> } = mapiData;
    if (users.length) {
      return applyLoggedInUser(users[0], setLogo);
    }
  });
};
